import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Field } from "formik";
import moment from "moment";
import { CustomDatePicker, CustomTimePicker } from "../../../common/FormInputs";
import {
  C,
  SEMI,
  REG,
  Section,
  SegLabel,
  TabStrip,
  TabButton,
  TabLabel,
  TabSub,
  TabActiveBar,
  FieldBlock,
  FloatLabel,
  RequiredStar,
} from "../Styles";
import {
  Card,
  Grid,
  RoomName,
  RoomDesc,
  MetaRow,
  MetaPill,
} from "./StepVerify";
import useLoader from "../../../../hooks/useLoader";
import BookingService from "../../../../services/BookingService";
import AlertDialog from "../../../common/Alert";
import { calculateBookingCharges } from "./StepServices";
import { useBookingHolidays, renderHolidayDay } from "../../utils/holidays";

const toDateMoment = (v) => (v ? moment(v, "YYYY-MM-DD") : null);
const handleDateChange = (momentVal, setFieldValue, field) => {
  if (!momentVal || !momentVal.isValid()) {
    setFieldValue(field, "");
    return;
  }
  setFieldValue(field, momentVal.format("YYYY-MM-DD"));
};
const toTimeMoment = (v) => (v ? moment(v, "HH:mm:ss") : null);
const handleTimeChange = (momentVal, setFieldValue, field) => {
  if (!momentVal || !momentVal.isValid()) {
    setFieldValue(field, "");
    return;
  }
  setFieldValue(field, momentVal.format("HH:mm:ss"));
};

const DURATIONS = [
  { id: "halfday", label: "Half Day", sub: "3-hour session", hours: "3 hrs" },
  { id: "fullday", label: "Full Day", sub: "6-hour session", hours: "6 hrs" },
];

// ─── Helper: derive day-type and price from room + date + duration ─────────────

// Govt holidays are charged at the weekend tariff.
export const getDayType = (dateStr, holidays = {}) => {
  if (!dateStr) return null;
  const day = moment(dateStr, "YYYY-MM-DD").day(); // 0 = Sun, 6 = Sat
  return day === 0 || day === 6 || holidays[dateStr] ? "weekend" : "weekday";
};

export const getRoomPrice = (room, dateStr, duration, holidays) => {
  if (!room?.pricing || !dateStr || !duration) return null;
  const dayType = getDayType(dateStr, holidays);
  return room.pricing?.[dayType]?.[duration] ?? null;
};

// ─── NEW: Validate that the selected date+time is in the future ───────────────

/**
 * Returns an error string if the date+time combination is invalid,
 * or null if everything is fine.
 *
 * Rules:
 *  - If the selected date is today, the start time must be strictly
 *    after the current moment (at least 1 minute ahead).
 *  - Past dates are not reachable via minDate on the picker, but we
 *    guard here too just in case.
 */
export const validateFutureDateTime = (dateStr, timeStr, setError) => {
  if (!dateStr || !timeStr) return null; // nothing to validate yet

  const selectedDateTime = moment(
    `${dateStr} ${timeStr}`,
    "YYYY-MM-DD HH:mm:ss",
  );

  if (!selectedDateTime.isValid()) return null;

  const now = moment();

  if (selectedDateTime.isSameOrBefore(now)) {
    const isToday = moment(dateStr, "YYYY-MM-DD").isSame(now, "day");
    if (isToday) {
      setError(true);
      return `Please select a future time. The current time is ${now.format(
        "hh:mm A",
      )} — only times after this are allowed for today's bookings.`;
    }
    // Past date (shouldn't happen with minDate, but just in case)
    return "Please select a future date and time.";
  }

  setError(false);
  return null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const StepSchedule = ({
  handleBlur,
  setFieldValue,
  values,
  errors,
  touched,
  roomsPricingInfo,
  setError,
  bookingReference,
}) => {
  const selected = values.hearing_room;
  const { setLoader } = useLoader();
  const [availableRooms, setAvailableRooms] = useState([]);
  const [slotError, setSlotError] = useState({ open: false, message: "" });
  const holidays = useBookingHolidays();
  const holidayName = holidays[values.hearing_date];

  // ── NEW: future-time validation error ──────────────────────────────────────
  const dateTimeError = useMemo(
    () =>
      validateFutureDateTime(values.hearing_date, values.start_time, setError),
    [values.hearing_date, values.start_time, setError],
  );

  // Day-type label shown to the user
  const dayType = useMemo(
    () => getDayType(values.hearing_date, holidays),
    [values.hearing_date, holidays],
  );

  const dayTypeLabel = useMemo(() => {
    if (!dayType) return null;
    return dayType === "weekend" ? "Weekend" : "Weekday";
  }, [dayType]);

  // When date or duration changes, update the price on the selected room object
  const handleRoomSelect = (room) => {
    const price = getRoomPrice(
      room,
      values.hearing_date,
      values.session_duration,
      holidays,
    );
    setFieldValue("hearing_room", { ...room, resolvedPrice: price });
    setFieldValue("room_charge", price || 0);

    const charges = calculateBookingCharges(
      values.supportServices,
      values.cateringServices,
      price || 0,
      values.session_duration,
    );
    setFieldValue("service_charge", charges.service_charge);
    setFieldValue("sub_total", charges.sub_total);
    setFieldValue("gst_amount", charges.gst_amount);
    setFieldValue("total_amount", charges.total_amount);
  };

  // Re-resolve price whenever date/duration changes while a room is already picked
  React.useEffect(() => {
    if (!selected) return;
    const price = getRoomPrice(
      selected,
      values.hearing_date,
      values.session_duration,
      holidays,
    );
    if (price !== selected.resolvedPrice) {
      setFieldValue("hearing_room", { ...selected, resolvedPrice: price });
      setFieldValue("room_charge", price || 0);

      const charges = calculateBookingCharges(
        values.supportServices,
        values.cateringServices,
        price || 0,
        values.session_duration,
      );
      setFieldValue("service_charge", charges.service_charge);
      setFieldValue("sub_total", charges.sub_total);
      setFieldValue("gst_amount", charges.gst_amount);
      setFieldValue("total_amount", charges.total_amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.hearing_date, values.session_duration, holidays]);

  async function fetchAvailableRooms(
    hearing_date,
    start_time,
    session_duration,
  ) {
    try {
      setLoader({ state: true, message: "Fetching available rooms..." });


      let query = `?hearing_date=${hearing_date}&start_time=${start_time}`;
      if (bookingReference) {
        query += `&booking_reference=${encodeURIComponent(bookingReference)}`;
      } else {
        query += `&session_duration=${session_duration}`;
      }
      const res = await BookingService.fetchAvailableRooms(query);

      // The slots API returns this response when the selected time is outside
      // the permitted booking window.
      if (
        res?.popup ||
        res?.success === false ||
        res?.status === false ||
        res?.error
      ) {
        clearUnavailableSlot();
        setSlotError({
          open: true,
          message: res?.message || "The selected start time is not available.",
        });
        return;
      }

      setAvailableRooms(res?.available || []);
    } catch (err) {
      clearUnavailableSlot();
      setSlotError({
        open: true,
        message: err?.message || err || "Unable to fetch available rooms.",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  function clearUnavailableSlot() {
    setAvailableRooms([]);
    setFieldValue("start_time", "");
    setFieldValue("hearing_room", null);
    setFieldValue("room_charge", 0);
  }

  React.useEffect(() => {
    const { hearing_date, start_time, session_duration } = values;

    // ── NEW: skip room fetch if the date+time is not valid/future ─────────
    if (hearing_date && start_time && session_duration && !dateTimeError) {
      fetchAvailableRooms(hearing_date, start_time, session_duration);
    }

    // If dateTimeError is present, clear previously loaded rooms so stale
    // results don't linger on screen.
    if (dateTimeError) {
      setAvailableRooms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.hearing_date,
    values.start_time,
    values.session_duration,
    dateTimeError,
  ]);

  const filteredRooms = useMemo(() => {
    if (!availableRooms?.length) return [];
    return roomsPricingInfo?.filter((room) =>
      availableRooms.includes(room.roomName),
    );
  }, [availableRooms, roomsPricingInfo]);

  return (
    <>
      <AlertDialog
        isOpen={slotError.open}
        heading="Booking time unavailable"
        desc={slotError.message}
        primaryBtnText="Okay"
        secondaryBtnText=""
        clickPrimaryBtn={() => setSlotError({ open: false, message: "" })}
        onDialogClose={() => setSlotError({ open: false, message: "" })}
      />
      {/* ── Date & Time ── */}
      <Section $delay="0s">
        <SegLabel>Date &amp; Time</SegLabel>
        <FieldGrid>
          <Field name="hearing_date">
            {({ meta }) => (
              <FieldBlock>
                <FloatLabel htmlFor="hearing_date">
                  Hearing Date <RequiredStar>*</RequiredStar>
                </FloatLabel>
                <CustomDatePicker
                  value={toDateMoment(values.hearing_date)}
                  onChange={(momentVal) =>
                    handleDateChange(momentVal, setFieldValue, "hearing_date")
                  }
                  format="DD/MM/YYYY"
                  minDate={moment()}
                  maxDate={moment().add(3, "months")}
                  renderDay={renderHolidayDay(holidays)}
                  clearable
                  inputVariant="outlined"
                  className="input-white"
                  error={touched.hearing_date && !!errors.hearing_date}
                  helperText={touched.hearing_date && errors.hearing_date}
                  style={{ marginTop: 6 }}
                  emptyLabel="DD/MM/YYYY"
                />
              </FieldBlock>
            )}
          </Field>

          <Field name="start_time">
            {({ meta }) => (
              <FieldBlock>
                <FloatLabel htmlFor="start_time">
                  Start Time <RequiredStar>*</RequiredStar>
                </FloatLabel>
                <CustomTimePicker
                  value={toTimeMoment(values.start_time)}
                  onChange={(momentVal) =>
                    handleTimeChange(momentVal, setFieldValue, "start_time")
                  }
                  clearable
                  inputVariant="outlined"
                  className="input-white"
                  ampm={true}
                  onBlur={handleBlur}
                  // ── NEW: mark the picker as errored when dateTimeError fires
                  error={
                    (touched.start_time && !!errors.start_time) ||
                    !!dateTimeError
                  }
                  helperText={touched.start_time && errors.start_time}
                  style={{ marginTop: 6 }}
                  emptyLabel="HH:MM"
                />
              </FieldBlock>
            )}
          </Field>
        </FieldGrid>

        {/* ── NEW: Future-time validation banner ─────────────────────────── */}
        {dateTimeError && (
          <DateTimeErrorBanner>
            <DateTimeErrorIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 8v4M12 16h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </DateTimeErrorIcon>
            <div>
              <DateTimeErrorTitle>Invalid Start Time</DateTimeErrorTitle>
              <DateTimeErrorMsg>{dateTimeError}</DateTimeErrorMsg>
            </div>
          </DateTimeErrorBanner>
        )}

        {/* Day-type indicator */}
        {values.hearing_date && dayTypeLabel && !dateTimeError && (
          <DayTypeBanner $weekend={dayType === "weekend"}>
            <DayTypeIcon>
              {dayType === "weekend" ? (
                // sun icon
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                // briefcase icon
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="2"
                    y="7"
                    width="20"
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              )}
            </DayTypeIcon>
            <span>
              <strong>{dayTypeLabel} pricing</strong> will apply to room
              bookings on this date
              {holidayName && ` (Govt. holiday – ${holidayName})`}.
            </span>
          </DayTypeBanner>
        )}
      </Section>

      {/* ── Session Duration ── */}
      <Section $delay="0.05s">
        <SegLabel>Session Duration</SegLabel>
        <TabStrip>
          {DURATIONS.map((d) => {
            const active = values.session_duration === d.id;
            return (
              <TabButton
                key={d.id}
                type="button"
                $active={active}
                onClick={() => setFieldValue("session_duration", d.id)}
              >
                <TabLabel $active={active}>{d.label}</TabLabel>
                <TabSub $active={active}>{d.sub}</TabSub>
                <DurHours $active={active}>{d.hours}</DurHours>
                <TabActiveBar $active={active} />
              </TabButton>
            );
          })}
        </TabStrip>
      </Section>

      {/* ── Available Rooms ── */}
      {/* ── NEW: only show rooms section when there is no dateTimeError ── */}
      {values?.hearing_date &&
        values?.start_time &&
        values?.session_duration &&
        !dateTimeError && (
          <Section $delay="0.08s">
            <SegLabel>
              Available Rooms
              {dayTypeLabel && values.session_duration && (
                <PricingContext>
                  {dayTypeLabel} ·{" "}
                  {values.session_duration === "halfday"
                    ? "Half Day"
                    : "Full Day"}{" "}
                  pricing
                </PricingContext>
              )}
            </SegLabel>
            <Grid>
              {filteredRooms?.length > 0 ? (
                filteredRooms?.map((r, i) => {
                  const active = selected?.roomName === r.roomName;
                  const resolvedPrice = getRoomPrice(
                    r,
                    values.hearing_date,
                    values.session_duration,
                    holidays,
                  );

                  return (
                    <Card
                      key={r.roomName}
                      type="button"
                      $active={active}
                      $i={i}
                      onClick={() => handleRoomSelect(r)}
                    >
                      <CardImage style={{ backgroundImage: `url(${r.image})` }}>
                        <ImageOverlay />
                        <ImageBadge>
                          {active ? "✓ Selected" : r.displayName.toUpperCase()}
                        </ImageBadge>
                        <ImageSub>
                          {r.size && `${r.size} · `}
                          {r.capacity} seats
                        </ImageSub>
                      </CardImage>

                      <CardBody>
                        <TopRow>
                          <RoomName $active={active}>{r.displayName}</RoomName>
                          {resolvedPrice != null ? (
                            <PriceStack>
                              <Price $active={active}>
                                ₹ {resolvedPrice.toLocaleString()}
                              </Price>
                              <PriceSub $active={active}>
                                {dayTypeLabel} ·{" "}
                                {values.session_duration === "halfday"
                                  ? "Half Day"
                                  : "Full Day"}
                              </PriceSub>
                            </PriceStack>
                          ) : (
                            <Price $active={active}>—</Price>
                          )}
                        </TopRow>

                        {r.description && (
                          <RoomDesc $active={active}>{r.description}</RoomDesc>
                        )}

                        {/* Pricing comparison chip */}
                        {resolvedPrice != null && (
                          <PricingBreakdown>
                            {["halfday", "fullday"].map((dur) => {
                              const p = r.pricing?.[dayType]?.[dur];
                              const isSelected =
                                values.session_duration === dur;
                              return (
                                p != null && (
                                  <PriceChip
                                    key={dur}
                                    $selected={isSelected}
                                    $active={active}
                                  >
                                    {dur === "halfday" ? "½ Day" : "Full Day"}
                                    <PriceChipVal $selected={isSelected}>
                                      ₹{p.toLocaleString()}
                                    </PriceChipVal>
                                  </PriceChip>
                                )
                              );
                            })}
                          </PricingBreakdown>
                        )}

                        <MetaRow>
                          <MetaPill $primary $active={active}>
                            Up to {r.capacity} attendees
                          </MetaPill>
                          <MetaPill $active={active}>+ 3 Arbitrators</MetaPill>
                          {r.size && (
                            <MetaPill $active={active}>{r.size}</MetaPill>
                          )}
                          {r.isBackupRoom && (
                            <MetaPill $active={active}>Backup</MetaPill>
                          )}
                        </MetaRow>

                        {r.facilities?.length > 0 && (
                          <>
                            <Divider $active={active} />
                            <FacilityTitle>FACILITIES INCLUDED</FacilityTitle>
                            <MetaRow>
                              {r.facilities.slice(0, 3).map((f, idx) => (
                                <MetaPill key={idx} $active={active}>
                                  {f}
                                </MetaPill>
                              ))}
                              {r.facilities.length > 3 && (
                                <MetaPill $active={active}>
                                  +{r.facilities.length - 3} More
                                </MetaPill>
                              )}
                            </MetaRow>
                          </>
                        )}
                      </CardBody>
                    </Card>
                  );
                })
              ) : (
                <NoRooms>No rooms available for selected date & time</NoRooms>
              )}
            </Grid>
          </Section>
        )}

      {touched.hearing_room && errors.hearing_room && (
        <DateTimeErrorBanner>
          <DateTimeErrorIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 8v4M12 16h.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </DateTimeErrorIcon>
          <div>
            <DateTimeErrorMsg>{errors.hearing_room}</DateTimeErrorMsg>
          </div>
        </DateTimeErrorBanner>
      )}

      {!(values?.hearing_date && values?.start_time) && (
        <SchedulePrompt>
          <SchedulePromptIcon>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect
                x="3"
                y="4"
                width="18"
                height="18"
                rx="3"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M3 10h18M8 2v4M16 2v4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </SchedulePromptIcon>
          Select a date and start time above to see available rooms.
        </SchedulePrompt>
      )}
    </>
  );
};

export default StepSchedule;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 8px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DurHours = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  padding: 3px 12px;
  border-radius: 99px;
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  background: ${(p) => (p.$active ? "rgba(0,131,140,0.25)" : C.navyAlpha)};
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.85)" : C.mutedMid)};
  border: 1px solid ${(p) => (p.$active ? "rgba(0,131,140,0.35)" : C.line)};
  transition: all 0.22s;
`;

const DateTimeErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 11px 14px;
  border-radius: 8px;
  background: rgba(220, 53, 69, 0.07);
  border: 1px solid rgba(220, 53, 69, 0.28);
  color: #b91c2c;
`;

const DateTimeErrorIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  color: #dc3545;
`;

const DateTimeErrorTitle = styled.div`
  font-family: ${SEMI};
  font-size: 12.5px;
  font-weight: 700;
  margin-bottom: 2px;
  color: #b91c2c;
`;

const DateTimeErrorMsg = styled.div`
  font-family: ${REG};
  font-size: 12px;
  line-height: 1.5;
  color: #b91c2c;
  opacity: 0.9;
`;

// Day-type banner below date picker
const DayTypeBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 9px 14px;
  border-radius: 8px;
  font-family: ${REG};
  font-size: 12.5px;
  line-height: 1.5;
  background: ${(p) =>
    p.$weekend ? "rgba(246,180,54,0.08)" : "rgba(0,131,140,0.07)"};
  border: 1px solid
    ${(p) => (p.$weekend ? "rgba(246,180,54,0.24)" : "rgba(0,131,140,0.18)")};
  color: ${(p) => (p.$weekend ? "#7a5200" : C.teal)};

  strong {
    font-family: ${SEMI};
    font-weight: 700;
  }
`;

const DayTypeIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

// Pricing context next to section label
const PricingContext = styled.span`
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 99px;
  background: rgba(0, 131, 140, 0.08);
  border: 1px solid rgba(0, 131, 140, 0.18);
  color: ${C.teal};
  margin-left: 10px;
  letter-spacing: 0.02em;
  vertical-align: middle;
`;

// Price with sub-label
const PriceStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

export const Price = styled.div`
  font-family: ${SEMI};
  font-weight: 800;
  font-size: 15px;
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.85)" : C.teal)};
  transition: color 0.2s;
`;

const PriceSub = styled.div`
  font-family: ${REG};
  font-size: 10px;
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.5)" : C.muted)};
  letter-spacing: 0.02em;
`;

// Mini pricing breakdown chips inside each room card
const PricingBreakdown = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const PriceChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 99px;
  font-family: ${SEMI};
  font-size: 10.5px;
  font-weight: 600;
  border: 1px solid
    ${(p) =>
      p.$selected
        ? p.$active
          ? "rgba(255,255,255,0.3)"
          : "rgba(0,131,140,0.35)"
        : C.line};
  background: ${(p) =>
    p.$selected
      ? p.$active
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,131,140,0.1)"
      : "transparent"};
  color: ${(p) =>
    p.$selected
      ? p.$active
        ? "rgba(255,255,255,0.9)"
        : C.teal
      : p.$active
        ? "rgba(255,255,255,0.45)"
        : C.muted};
  transition: all 0.2s;
`;

const PriceChipVal = styled.span`
  font-weight: 800;
  font-size: 11px;
`;

export const CardImage = styled.div`
  height: 130px;
  border-radius: 12px 12px 0 0;
  background-size: cover;
  background-position: center;
  position: relative;
`;

export const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(20, 30, 60, 0.5),
    rgba(20, 30, 60, 0.88)
  );
  border-radius: 12px 12px 0 0;
`;

const NoRooms = styled.div`
  width: 100%;
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
  font-family: ${SEMI};
  font-size: 14px;
  color: ${C.muted};
`;

const ImageBadge = styled.div`
  position: absolute;
  bottom: 38px;
  left: 14px;
  color: #fff;
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.02em;
`;

export const ImageSub = styled.div`
  position: absolute;
  bottom: 14px;
  left: 14px;
  font-family: ${REG};
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
`;

export const CardBody = styled.div`
  padding: 14px 16px 16px;
`;

export const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${(p) => (p.$active ? "rgba(255,255,255,0.1)" : C.line)};
  margin: 10px 0;
`;

export const FacilityTitle = styled.div`
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${C.muted};
  margin-bottom: 8px;
`;

const SchedulePrompt = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  background: ${C.surface};
  border: 1.5px dashed ${C.line};
  font-family: ${REG};
  font-size: 13px;
  color: ${C.muted};
  margin-top: 4px;
`;

const SchedulePromptIcon = styled.div`
  color: ${C.mutedLight};
  flex-shrink: 0;
`;
